import { createContext, useContext } from 'react';

import { type FieldClaimDisplay } from './fieldClaimDisplay';

/** What "Edit anyway" acts on: the input the override unblocks. */
export interface EditBlockedTarget {
  /** Focus the input; deferred to an effect so the unblocking re-render lands first. */
  focus: () => void;
  /** Mount a not-yet-rendered draft input (an add-affordance); runs with the override. */
  open?: () => void;
}

export interface TemplateFieldPresenceValue {
  /** Every displayed claim on a field (earliest first), names and colors resolved. */
  claimsFor(fieldKey: string): FieldClaimDisplay[];
  /** Whether that claim currently stops the local user editing the field. */
  isBlocked(fieldKey: string): boolean;
  fieldFocused(fieldKey: string): void;
  fieldBlurred(fieldKey: string): void;
  /** Tap on a blocked field: haptic + toast offering "Edit anyway". */
  editBlockedPress(fieldKey: string, target?: EditBlockedTarget): void;
  /**
   * Remove attempt on a claimed step: haptic + toast offering "Delete anyway".
   * The toast names every claimant across the step's fields.
   */
  deleteBlockedPress(fieldKeys: string[], removeAnyway: () => void): void;
}

/** Inert value so claimable components render unchanged outside the provider. */
const INERT: TemplateFieldPresenceValue = {
  claimsFor: () => [],
  isBlocked: () => false,
  fieldFocused: () => {},
  fieldBlurred: () => {},
  editBlockedPress: () => {},
  deleteBlockedPress: () => {},
};

export const TemplateFieldPresenceContext =
  createContext<TemplateFieldPresenceValue>(INERT);

export function useTemplateFieldPresence(): TemplateFieldPresenceValue {
  return useContext(TemplateFieldPresenceContext);
}
